import {
  Badge,
  Divider,
  ProgressBar,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import type { BriefData } from "../../../api/chat";
import type { StagedFile } from "../useConversation";
import { buildRecapFields, countFilledFields, GENERAL_FIELDS } from "../catalog";

const useStyles = makeStyles({
  panel: {
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
    ...shorthands.padding("16px"),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    height: "fit-content",
    position: "sticky",
    top: "16px",
  },
  header: { display: "flex", flexDirection: "column", rowGap: "2px" },
  progressRow: { display: "flex", flexDirection: "column", rowGap: "6px" },
  cards: { display: "flex", flexDirection: "column", rowGap: "8px" },
  card: {
    ...shorthands.padding("8px", "10px"),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground2,
  },
  cardOk: { ...shorthands.borderColor(tokens.colorPaletteGreenBorder2) },
  cardRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: "8px",
  },
  label: { fontWeight: tokens.fontWeightSemibold },
  category: { color: tokens.colorNeutralForeground3, fontStyle: "italic" },
  value: { color: tokens.colorNeutralForeground2, wordBreak: "break-word" },
  pending: { color: tokens.colorNeutralForeground4, fontStyle: "italic" },
  hint: { color: tokens.colorNeutralForeground3 },
});

export function RecapPanel({
  briefData,
  stagedFiles,
}: {
  briefData: BriefData;
  stagedFiles: StagedFile[];
}) {
  const styles = useStyles();
  const filled = countFilledFields(briefData, GENERAL_FIELDS);
  const total = GENERAL_FIELDS.length;
  const pct = total > 0 ? Math.round((filled / total) * 100) : 0;
  const fields = buildRecapFields(briefData);

  return (
    <aside className={styles.panel} aria-label="Récapitulatif du brief">
      <div className={styles.header}>
        <Text size={500} weight="semibold">
          📋 Brief
        </Text>
        <Text className={styles.hint} size={200}>
          Récapitulatif structuré en temps réel.
        </Text>
      </div>

      <div className={styles.progressRow}>
        <ProgressBar value={total > 0 ? filled / total : 0} thickness="large" />
        <Text size={200} className={styles.hint}>
          {pct}% complété — {filled}/{total} champs principaux
        </Text>
        {stagedFiles.length > 0 && (
          <Text size={200} className={styles.hint}>
            📎 {stagedFiles.length} fichier(s) temporairement associé(s) à ce brief
          </Text>
        )}
      </div>

      <Divider />

      <div className={styles.cards}>
        {fields.map((f) => (
          <div key={`${f.category}:${f.key}`} className={`${styles.card} ${f.ok ? styles.cardOk : ""}`}>
            <div className={styles.cardRow}>
              <Text size={200} className={styles.label}>
                {f.label} <span className={styles.category}>({f.category})</span>
              </Text>
              <Badge appearance="tint" color={f.ok ? "success" : "informative"} size="small">
                {f.ok ? "OK" : "—"}
              </Badge>
            </div>
            <Text size={200} className={f.ok ? styles.value : styles.pending}>
              {f.value}
            </Text>
          </div>
        ))}
      </div>
    </aside>
  );
}
